import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { getCookie, deleteCookie } from "../utils/cookies";
import dummyData from "../dummy-backend.json";

const isDemo = process.env.REACT_APP_DEMO_MODE === "true";

/**
 * Adds the auth cookie and `x-platform: web` to every storefront API call.
 * Existing APIs treat the header as optional; the backend rejects invalid values.
 *
 * @param {Headers} headers - RTK Query headers object to mutate.
 * @returns {Headers} The same headers object.
 */
function applyStorefrontHeaders(headers) {
  const token = getCookie("auth_token");
  if (token) {
    headers.set("authorization", `Bearer ${token}`);
  }
  headers.set("x-platform", "web");
  return headers;
}

const demoBaseQuery = async (args, api, extraOptions) => {
  if (typeof args === "string" || typeof args?.url === "string") {
    const url = typeof args === "string" ? args : args.url;

    // For payment endpoints in demo mode, allow real API calls
    if (
      isDemo &&
      (url.includes("/payment/") ||
        url.includes("/shipment/") ||
        url.includes("/checkout/"))
    ) {
      // Use real API for payment and shipment endpoints even in demo mode
      const realBaseQuery = fetchBaseQuery({
        baseUrl: process.env.REACT_APP_URL,
        prepareHeaders: (headers) => applyStorefrontHeaders(headers),
      });
      const result = await realBaseQuery(args, api, extraOptions);

      // Handle 401 Unauthorized
      if (result.error && result.error.status === 401) {
        const currentPath = window.location.pathname;
        if (
          !currentPath.includes("/login") &&
          !currentPath.includes("/register")
        ) {
          const search = window.location.search || "";
          window.location.href = `/login${search}`;
        }
      }

      return result;
    }

    // Store Info
    if (url.includes("/store/info")) {
      return { data: dummyData.storeInfo };
    }
    // Inventory Packs - Let real API handle this even in demo mode
    if (url.includes("/inventory/packs")) {
      // Use real API for inventory packs
      const realBaseQuery = fetchBaseQuery({
        baseUrl: process.env.REACT_APP_URL,
        prepareHeaders: (headers) => applyStorefrontHeaders(headers),
      });
      const result = await realBaseQuery(args, api, extraOptions);

      // Handle 401 Unauthorized - preserve store_id (and full search) when redirecting to login
      if (result.error && result.error.status === 401) {
        const currentPath = window.location.pathname;
        if (
          !currentPath.includes("/login") &&
          !currentPath.includes("/register")
        ) {
          const search = window.location.search || "";
          window.location.href = `/login${search}`;
        }
      }

      return result;
    }
    // Auth
    if (url.includes("/auth/send-otp")) {
      return {
        data: {
          success: true,
          message: "OTP sent (demo mode)",
          result: { email: args.body?.email },
        },
      };
    }
    if (url.includes("/auth/verify-otp")) {
      return {
        data: {
          success: true,
          message: "Login successful (demo mode)",
          result: {
            user: { id: 1, email: args.body?.email, name: "Demo User" },
            token: "demo_token",
          },
        },
      };
    }
    if (url.includes("/auth/register")) {
      return {
        data: {
          success: true,
          message: "Registration successful (demo mode)",
          result: {
            user: {
              id: Date.now(),
              email: args.body?.email,
              name: `${args.body?.firstName} ${args.body?.lastName}`,
              phone: args.body?.phone,
            },
            token: "demo_token",
          },
        },
      };
    }
    if (url.includes("/auth/verify-session")) {
      return {
        data: {
          success: true,
          message: "Session verified",
          result: {
            valid: true,
            user: { id: 1, email: "demo@demo.com", name: "Demo User" },
          },
        },
      };
    }
  }
  // Default fallback
  return {
    data: { success: false, message: "Endpoint not found in demo mode" },
  };
};

// Custom base query that properly handles FormData and redirects
const customBaseQuery = async (args, api, extraOptions) => {
  // Handle Azure SAML login redirect manually
  if (args?.url?.includes("/auth/azure/saml/login")) {
    const baseUrl = process.env.REACT_APP_URL;
    const url = `${baseUrl}${args.url}`;

    try {
      const response = await fetch(url, {
        method: "GET",
        redirect: "manual", // Don't follow redirects automatically
        headers: { "x-platform": "web" },
      });

      // Handle 401 Unauthorized - preserve store_id (and full search) when redirecting to login
      if (response.status === 401) {
        const currentPath = window.location.pathname;
        if (
          !currentPath.includes("/login") &&
          !currentPath.includes("/register")
        ) {
          const search = window.location.search || "";
          window.location.href = `/login${search}`;
        }
        return { error: { status: 401, data: "Unauthorized" } };
      }

      // Handle 302/301 redirect
      if (response.status === 302 || response.status === 301) {
        const location = response.headers.get("Location");
        if (location) {
          return { data: { redirectUrl: location } };
        }
      }

      // Try to get redirect URL from response body
      const contentType = response.headers.get("content-type");
      if (contentType?.includes("application/json")) {
        const data = await response.json();
        const redirectUrl =
          data.url || data.redirectUrl || data.redirect_url || data.location;
        if (redirectUrl) {
          return { data: { redirectUrl } };
        }
        return { data };
      } else {
        const text = response.url;
        // Check if text is a URL
        if (
          text.trim().startsWith("http://") ||
          text.trim().startsWith("https://")
        ) {
          return { data: { redirectUrl: text.trim() } };
        }
        return { data: { redirectUrl: null, data: text } };
      }
    } catch (error) {
      return { error: { status: "FETCH_ERROR", error: error.message } };
    }
  }

  const baseQuery = fetchBaseQuery({
    baseUrl: process.env.REACT_APP_URL,
    credentials: "include",
    prepareHeaders: (headers) => applyStorefrontHeaders(headers),
  });

  const result = await baseQuery(args, api, extraOptions);

  // Handle 401 Unauthorized - preserve store_id (and full search) when redirecting to login
  if (result.error && result.error.status === 401) {
    const currentPath = window.location.pathname;
    if (!currentPath.includes("/login") && !currentPath.includes("/register")) {
      const search = window.location.search || "";
      window.location.href = `/login${search}`;
    }
  }

  return result;
};

export const apiSlice = createApi({
  reducerPath: "api",
  baseQuery: isDemo ? demoBaseQuery : customBaseQuery,
  endpoints: (builder) => ({
    resolveStoreByHost: builder.query({
      query: (storeIdParam) => {
        const params =
          storeIdParam != null && String(storeIdParam).trim() !== ""
            ? { store_id: storeIdParam }
            : {};
        return { url: `/store/resolve-host`, params };
      },
    }),

    getCategories: builder.query({
      query: (storeId) => `/inventory/categories/${storeId}`,
    }),

    getInventoryPacks: builder.query({
      query: ({
        customerId,
        storeId,
        page = 1,
        limit = 28,
        search = null,
        sort_by = "created_date",
        sort_order = "DESC",
        category_id = null,
      }) => {
        // Filter out null, undefined, and empty string values
        const params = {
          page,
          limit,
          sort_by,
          sort_order,
        };

        // Add storeId if provided
        if (
          storeId &&
          storeId !== "" &&
          storeId !== "null" &&
          storeId !== "undefined"
        ) {
          params.storeId = storeId;
        }

        // Only add search if it's a valid non-empty string
        if (
          search &&
          search !== "" &&
          search !== "null" &&
          search !== "undefined"
        ) {
          params.search = search;
        }

        // Only add category_id if it's a valid non-empty value (can be comma-separated IDs)
        if (
          category_id &&
          category_id !== "" &&
          category_id !== "null" &&
          category_id !== "undefined"
        ) {
          params.category_id = category_id;
        }

        return {
          url: `/inventory/packs/${customerId}`,
          params,
        };
      },
    }),

    getInventoryPackDetails: builder.query({
      query: ({ customerId, storeId, packId, type }) => {
        const params = {};
        // Add storeId if provided
        if (
          storeId &&
          storeId !== "" &&
          storeId !== "null" &&
          storeId !== "undefined"
        ) {
          params.storeId = storeId;
        }
        // Add type query parameter if provided
        if (type) {
          params.type = type;
        }
        return {
          url: `/inventory/packs/${customerId}/${packId}`,
          params,
        };
      },
    }),

    sendOTP: builder.mutation({
      query: ({ customerId, storeId, email }) => {
        const params = {};
        if (
          storeId &&
          storeId !== "" &&
          storeId !== "null" &&
          storeId !== "undefined"
        ) {
          params.storeId = storeId;
        }
        return {
          url: `/auth/send-otp/${customerId}`,
          method: "POST",
          params,
          body: { email },
        };
      },
    }),

    verifyOTP: builder.mutation({
      query: ({ customerId, storeId, email, otp }) => {
        const params = {};
        if (
          storeId &&
          storeId !== "" &&
          storeId !== "null" &&
          storeId !== "undefined"
        ) {
          params.storeId = storeId;
        }
        return {
          url: `/auth/verify-otp/${customerId}`,
          method: "POST",
          params,
          body: { email, otp },
        };
      },
    }),

    loginWithAzure: builder.mutation({
      query: () => {
        return {
          url: `/auth/azure/saml/login`,
          method: "GET",
        };
      },
    }),
    register: builder.mutation({
      query: ({ customerId, storeId, ...userData }) => {
        const params = {};
        if (
          storeId &&
          storeId !== "" &&
          storeId !== "null" &&
          storeId !== "undefined"
        ) {
          params.storeId = storeId;
        }
        return {
          url: `/auth/register/${customerId}`,
          method: "POST",
          params,
          body: userData,
        };
      },
    }),

    verifySession: builder.query({
      query: ({ customerId, storeId }) => {
        const params = {};
        if (
          storeId &&
          storeId !== "" &&
          storeId !== "null" &&
          storeId !== "undefined"
        ) {
          params.storeId = storeId;
        }
        return {
          url: `/auth/verify-session/${customerId}`,
          method: "GET",
          params,
        };
      },
    }),

    logout: builder.mutation({
      query: () => {
        return {
          url: `/auth/logout`,
          method: "POST",
        };
      },
    }),

    placeCheckout: builder.mutation({
      query: ({ customerId, userId, storeId, ...body }) => {
        const params = {};
        if (
          storeId &&
          storeId !== "" &&
          storeId !== "null" &&
          storeId !== "undefined"
        ) {
          params.storeId = storeId;
        }
        return {
          url: `/checkout/${customerId}/place/${userId}`,
          method: "POST",
          params,
          body,
        };
      },
    }),

    abandonCheckout: builder.mutation({
      query: ({ customerId, userId, storeId, place_id }) => {
        const params = {};
        if (
          storeId &&
          storeId !== "" &&
          storeId !== "null" &&
          storeId !== "undefined"
        ) {
          params.storeId = storeId;
        }
        return {
          url: `/checkout/${customerId}/abandon/${userId}`,
          method: "POST",
          params,
          body: { place_id },
        };
      },
    }),

    // Create unified shipment (replaces both createShipment and createProductShipment)
    createUnifiedShipment: builder.mutation({
      query: ({ customerId, storeId, ...shipmentData }) => {
        const params = {};
        if (
          storeId &&
          storeId !== "" &&
          storeId !== "null" &&
          storeId !== "undefined"
        ) {
          params.storeId = storeId;
        }
        return {
          url: `/shipment/${customerId}/shipmentRequest/unified/${shipmentData.user_id}`,
          method: "POST",
          params,
          body: shipmentData,
        };
      },
    }),

    getShipmentHistory: builder.query({
      query: ({ customerId, userId, page, storeId }) => ({
        url: `/shipment/${customerId}/shipments-history/${userId}`,
        method: "GET",
        params: { page, storeId },
      }),
    }),

    // Create Razorpay payment order
    createPaymentOrder: builder.mutation({
      query: ({
        amount,
        shipment_id,
        customer_id,
        currency_code,
        checkout_snapshot,
        place_id,
      }) => ({
        url: `/payment/createOrder`,
        method: "POST",
        // checkout_snapshot is stored on payment_order for support lookup.
        // place_id links Razorpay to the hidden place.
        body: {
          amount,
          shipment_id,
          customer_id,
          currency_code,
          checkout_snapshot,
          ...(place_id ? { place_id } : {}),
        },
      }),
    }),

    // Verify Razorpay payment
    verifyPayment: builder.mutation({
      query: ({
        razorpay_payment_id,
        razorpay_order_id,
        razorpay_signature,
      }) => ({
        url: `/payment/verify`,
        method: "POST",
        body: { razorpay_payment_id, razorpay_order_id, razorpay_signature },
      }),
    }),

    // Check payment status
    checkPaymentStatus: builder.query({
      query: (paymentId) => `/payment/status/${paymentId}`,
    }),

    // Recover payment
    recoverPayment: builder.mutation({
      query: ({ payment_id, order_id }) => ({
        url: `/payment/recover`,
        method: "POST",
        body: { razorpay_payment_id: payment_id, razorpay_order_id: order_id },
      }),
    }),

    // Record shipment payment
    recordShipmentPayment: builder.mutation({
      query: ({
        shipment_ids,
        rzp_order_id,
        points_redeemed,
        user_id,
        storeId,
        customerId,
      }) => ({
        url: `/payment/record-shipment-payment/${user_id}`,
        method: "POST",
        body: {
          shipment_ids,
          rzp_order_id,
          points_redeemed,
          store_id: storeId,
          customer_id: customerId,
        },
      }),
    }),

    // Upload multiple files
    uploadFiles: builder.mutation({
      query: (formData) => {
        return {
          url: `/images/multiple`,
          method: "POST",
          body: formData,
        };
      },
    }),
    // Submit support request
    createSupportTicket: builder.mutation({
      query: (supportPayload) => ({
        url: `/support/ticket`,
        method: "POST",
        body: supportPayload,
      }),
    }),

    // Get user wallet points
    getPoints: builder.query({
      query: ({ storeId, customerId, userId }) => ({
        url: `/auth/points/${storeId}/${customerId}/${userId}`,
        method: "GET",
        params: { storeId },
      }),
    }),

    // Get user addresses - GET /addresses/:customer_id/:user_id
    getAddresses: builder.query({
      query: ({ storeId, customerId, userId }) => ({
        url: `user/addresses/${customerId}/${userId}`,
        method: "GET",
        params: { storeId },
      }),
    }),

    // Create user address
    createAddress: builder.mutation({
      query: ({ userId, storeId, customerId, ...addressData }) => {
        const params = {};
        if (
          storeId &&
          storeId !== "" &&
          storeId !== "null" &&
          storeId !== "undefined"
        ) {
          params.storeId = storeId;
        }
        if (
          customerId &&
          customerId !== "" &&
          customerId !== "null" &&
          customerId !== "undefined"
        ) {
          params.customer_id = customerId;
        }
        return {
          url: `user/addresses/${customerId}/${userId}`,
          method: "POST",
          params,
          body: addressData,
        };
      },
    }),

    // Update user address - PUT /address/:id/:customer_id/:user_id
    updateAddress: builder.mutation({
      query: ({ storeId, addressId, customerId, userId, ...addressData }) => ({
        url: `/user/address/${addressId}/${customerId}/${userId}`,
        method: "PUT",
        params: { storeId },
        body: addressData,
      }),
    }),

    // Delete user address - DELETE /address/:id/:customer_id
    deleteAddress: builder.mutation({
      query: ({ storeId, userId, addressId, customerId }) => ({
        url: `/user/address/${userId}/${addressId}`,
        params: { storeId: storeId, customer_id: customerId },
        method: "DELETE",
      }),
    }),
  }),
});

export const {
  useResolveStoreByHostQuery,
  useSendOTPMutation,
  useVerifyOTPMutation,
  useVerifySessionQuery,
  useLoginWithAzureMutation,
  useLogoutMutation,
  useGetInventoryPacksQuery,
  useCreateShipmentMutation,
  useGetShipmentHistoryQuery,
  useCreatePaymentOrderMutation,
  useVerifyPaymentMutation,
  useCheckPaymentStatusQuery,
  useRecoverPaymentMutation,
  useRecordShipmentPaymentMutation,
  useCreateProductShipmentMutation,
  useRegisterMutation,
  useGetCategoriesQuery,
  useGetInventoryPackDetailsQuery,
  usePlaceCheckoutMutation,
  useAbandonCheckoutMutation,
  useCreateUnifiedShipmentMutation,
  useCreateSupportTicketMutation,
  useUploadFilesMutation,
  useGetPointsQuery,
  useCreateAddressMutation,
  useGetAddressesQuery,
  useUpdateAddressMutation,
  useDeleteAddressMutation,
} = apiSlice;
